import { Box, Typography, Link as MUILink, Breadcrumbs } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getTextByLang } from "../../utils/formUtils";
import usePrevPath from "./usePrevPath";
import "./BreadCrumb.css";

const NON_CLICKABLE = new Set([
  "privatearea",
  "admindsb",
  "dataofferingdetail",
  "resourcedetail",
  "editdataoffering",
  "editresource",
  "editparticipant",
]);

const LABEL_ALIASES = {
  datamodels: "datamodel",
  mycontracts: "mycontract",
  dataservice: "mydataservice",
  policies: "mypolicies",
  participant: "participantDiscovery",
  participantmanagement: "participant.participantManagement",
  createparticipant: "addparticipant",
  editparticipant: "participant.editParticipant",
  offeringdata: "offeringData",
  createuser: "createuser.title",
};

const PRIVATE_AREA_KEYS = new Set([
  "mydatasets",
  "assetscontracts",
  "actionlog",
  "mydataservice",
  "mypolicies",
  "mycontracts",
  "profilePage.title",
  "registerequests",
  "createuser",
]);

const CHILD_TO_PARENT = {
  mydatasets: "privatearea",
  mycontracts: "privatearea",
  assetscontracts: "privatearea",
  actionlog: "privatearea",
  policies: "privatearea",
  dataservice: "privatearea",
  dataoffering: "home",
  datamodel: "home",
  offeringdata: "home",
  participantdiscovery: "home",
  participantmanagement: "home",
  createparticipant: "participantmanagement",
  editparticipant: "participantmanagement",
  createresource: "datamodel",
  createdataoffering: "dataoffering",
  activitytracking: "admindsb",
  notificationsalerts: "admindsb",
  backuprecovery: "admindsb",
  usercontrol: "admindsb",
  registerequests: "admindsb",
  createuser: "usercontrol",
};

function titleize(s) {
  return s.replace(/[-_]/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
}
function isNumericId(seg) {
  return /^\d+$/.test(seg);
}
function isUUID(seg) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    seg,
  );
}
function topLevelOf(pathname) {
  const parts = String(pathname || "")
    .split("/")
    .filter(Boolean);
  return (parts[0] || "").toLowerCase();
}
function cameFromPrivateArea(prevPath) {
  const top = topLevelOf(prevPath);
  return PRIVATE_AREA_KEYS.has(top) || top === "privatearea";
}

function getFrom(location) {
  const { state, search } = location;
  const params = new URLSearchParams(search);
  const from =
    (state && typeof state === "object" && state.from) ||
    params.get("from") ||
    null;
  return from ? String(from).toLowerCase() : null;
}

function buildCrumbs({ segments, t }) {
  let acc = "";
  return segments.map((raw) => {
    const segLower = raw.toLowerCase();
    acc += `/${raw}`;
    const key = LABEL_ALIASES[segLower] || segLower;

    let label;
    if (isNumericId(segLower)) label = `ID: ${raw}`;
    else if (isUUID(segLower)) label = `ID: ${raw.slice(0, 8)}…`;
    else label = t(key, { defaultValue: titleize(raw) });

    return { to: acc, label, seg: segLower, title: label };
  });
}

function getLocalizedValueOrEmpty(field) {
  const value = getTextByLang(field);
  if (!value || value === "Not specified.") return "";
  return value;
}

function overrideDetailLabel(crumbs, segments, state) {
  const isDetailPage = segments.some((s) =>
    [
      "dataofferingdetail",
      "resourcedetail",
      "editdataoffering",
      "editresource",
      "editparticipant",
    ].includes(s.toLowerCase()),
  );

  if (!isDetailPage || !crumbs.length) return crumbs;

  const translatedPreferredLabel = getLocalizedValueOrEmpty(
    state?.record?.preferredLabel,
  );
  const translatedParticipantName = getLocalizedValueOrEmpty(
    state?.record?.participant_name,
  );
  const translatedTitle = getLocalizedValueOrEmpty(state?.record?.title);
  const translatedResourceTitle = getLocalizedValueOrEmpty(
    state?.record?.resource_title,
  );
  const translatedName = getLocalizedValueOrEmpty(state?.record?.name);
  const translatedSchemeName = getLocalizedValueOrEmpty(
    state?.record?.identifier?.[0]?.schemeName,
  );

  const creatorLabel =
    translatedName ||
    translatedPreferredLabel ||
    translatedParticipantName ||
    getLocalizedValueOrEmpty(state?.record?.resource_name) ||
    translatedTitle ||
    translatedResourceTitle ||
    state?.record?.creator?.[0] ||
    translatedSchemeName ||
    state?.record?.identifier?.[0]?.notation ||
    (typeof state?.record === "string" ? state.record : null);

  if (!creatorLabel) return crumbs;

  const full = String(creatorLabel);
  const next = [...crumbs];
  next[next.length - 1] = {
    ...next[next.length - 1],
    label: full,
    title: full,
  };
  return next;
}

function injectParentCrumb({
  crumbs,
  firstSeg,
  fromLower,
  inferredFromPrivateArea,
  t,
}) {
  if (
    firstSeg === "editparticipant" &&
    CHILD_TO_PARENT[firstSeg] &&
    !crumbs.some((crumb) => crumb.seg === CHILD_TO_PARENT[firstSeg])
  ) {
    const parentKey = fromLower || CHILD_TO_PARENT[firstSeg];
    const resolvedParentKey = LABEL_ALIASES[parentKey] || parentKey;
    const parentLabel = t(resolvedParentKey, {
      defaultValue: titleize(parentKey),
    });

    return [
      {
        to: `/${parentKey}`,
        label: parentLabel,
        seg: parentKey,
        title: parentLabel,
      },
      ...crumbs,
    ];
  }

  if (
    crumbs.length === 1 &&
    firstSeg === "profilepage" &&
    (fromLower === "privatearea" || inferredFromPrivateArea)
  ) {
    const parentLabel = t("privatearea", { defaultValue: "Private Area" });
    return [
      { to: null, label: parentLabel, seg: "privatearea", title: parentLabel },
      ...crumbs,
    ];
  }

  if (
    crumbs.length === 1 &&
    firstSeg === "createresource" &&
    (fromLower === "datamodel" || fromLower === "datamodels")
  ) {
    return [
      {
        to: "/home",
        label: t("home", { defaultValue: "Home" }),
        seg: "home",
        title: "Home",
      },
      {
        to: "/datamodel",
        label: t("datamodel", { defaultValue: "Data Models" }),
        seg: "datamodel",
        title: "Data Models",
      },
      {
        to: "/createresource",
        label: t("createresource", { defaultValue: "Create Resource" }),
        seg: "createresource",
        title: "Create Resource",
      },
    ];
  }

  if (
    crumbs.length === 1 &&
    firstSeg === "createdataoffering" &&
    (fromLower === "dataoffering" || fromLower === "dataofferings")
  ) {
    return [
      {
        to: "/home",
        label: t("home", { defaultValue: "Home" }),
        seg: "home",
        title: "Home",
      },
      {
        to: "/dataoffering",
        label: t("dataoffering", { defaultValue: "Data Offerings" }),
        seg: "dataoffering",
        title: "Data Offerings",
      },
      {
        to: "/createdataoffering",
        label: t("createdataoffering", {
          defaultValue: "Create Data Offering",
        }),
        seg: "createdataoffering",
        title: "Create Data Offering",
      },
    ];
  }

  if (crumbs.length === 1 && CHILD_TO_PARENT[firstSeg]) {
    const parentKey = fromLower || CHILD_TO_PARENT[firstSeg];
    const resolvedParentKey = LABEL_ALIASES[parentKey] || parentKey;
    const parentLabel = t(resolvedParentKey, {
      defaultValue:
        parentKey === "privatearea" ? "Private Area" : titleize(parentKey),
    });

    const parentCrumb =
      parentKey === "privatearea"
        ? {
            to: null,
            label: parentLabel,
            seg: "privatearea",
            title: parentLabel,
          }
        : {
            to: `/${parentKey}`,
            label: parentLabel,
            seg: parentKey,
            title: parentLabel,
          };

    return [parentCrumb, ...crumbs];
  }

  return crumbs;
}

export default function BreadCrumb() {
  usePrevPath();
  const location = useLocation();
  const { pathname, state } = location;
  const { t } = useTranslation();

  const segments = pathname.split("/").filter(Boolean).map(decodeURIComponent);
  const firstSeg = (segments[0] || "").toLowerCase();

  const fromLower = getFrom(location);
  const prevPath = sessionStorage.getItem("prevPath") || "";
  const inferredFromPrivateArea = cameFromPrivateArea(prevPath);

  let crumbs = buildCrumbs({ segments, t });
  crumbs = overrideDetailLabel(crumbs, segments, state);
  crumbs = injectParentCrumb({
    crumbs,
    firstSeg,
    fromLower,
    inferredFromPrivateArea,
    t,
  });

  return (
    <Box className="breadcrumb-container" aria-label="Breadcrumb">
      <Breadcrumbs
        separator={<NavigateNextIcon className="breadcrumb-separator" />}
        maxItems={4}
        itemsAfterCollapse={2}
        itemsBeforeCollapse={1}
        className="breadcrumb-root"
      >
        {crumbs.map((c, idx) => {
          const isLast = idx === crumbs.length - 1;
          const clickable = !!c.to && !isLast && !NON_CLICKABLE.has(c.seg);

          if (clickable) {
            return (
              <MUILink
                key={`${c.to}-${idx}`}
                component={RouterLink}
                to={c.to}
                underline="none"
                className="breadcrumb-link"
                title={c.title || c.label}
              >
                <span className="breadcrumb-label">{c.label}</span>
              </MUILink>
            );
          }

          return (
            <Typography
              key={`${c.to ?? c.label}-${idx}`}
              className={isLast ? "breadcrumb-current" : "breadcrumb-text"}
              title={c.title || c.label}
            >
              <span className="breadcrumb-label">{c.label}</span>
            </Typography>
          );
        })}
      </Breadcrumbs>
    </Box>
  );
}
